import React from 'react';
import { Container, HBox, PuzzleButton, PuzzleDropDownButton, VBox } from "puzzle";
import { GridCellProps } from "@progress/kendo-react-grid";
import { GridColumn as KendoGridColumn } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { defaultCellRender, isActionAvailable, SUPPORTED_EDITORS } from "../../util/Util";
import ClaimDialogAbstract from "../../Abstract/ClaimDialog/ClaimDialogAbstract";
import { Notification } from "@progress/kendo-react-notification";
import { PROPS, STATE } from "../../Abstract/ClaimDialog/Types";
import KendoGridAbstract from "../../util/KendoGridAbstract/KendoGridAbstract";
import { DropDownButtonItemClickEvent } from "@progress/kendo-react-buttons";
import _ from "lodash";
import { DialogActionsBar } from "@progress/kendo-react-dialogs";

class ClaimTaskDialog<P, S> extends ClaimDialogAbstract<P & PROPS, STATE> {
  displayBadge = false;

  state = {
    data: [],
    claimLvl: '',
    showClaimAndOpen: true
  };

  moduleDidMount = () => {
    super.moduleDidMount();
    const { batchLanguagePhaseDtoList = [], claimLvl } = this.props.responseData.data;
    let showClaimAndOpen = true;
    if (batchLanguagePhaseDtoList[0]) {
      this.displayBadge = this.props.isClaim && !batchLanguagePhaseDtoList.every((current: any) => current.phaseName === batchLanguagePhaseDtoList[0].phaseName);
      showClaimAndOpen = this.getClaimAndOpenButtonState();
    }
    this.setState({
      data: batchLanguagePhaseDtoList,
      claimLvl: claimLvl,
      showClaimAndOpen
    })
  };

  /**
   * Renders claim and open button
   */
  claimAndOpenRender = (): JSX.Element | null => {
    const { submissionData } = this.props;
    const allParsable = _.get(submissionData, 'parsableState') === 'ALL_PARSABLE';
    let editors: string[] = _.get(submissionData, 'availableEditors') || [];
    const openDownloadEnabled = _.get(submissionData, 'openDownloadEnabled');
    editors = editors.filter((item: string) => _.includes(SUPPORTED_EDITORS, item));

    let render = null;
    if (this.state.showClaimAndOpen) {
      if (allParsable) {
        if (editors.length === 1) {
          render = <PuzzleButton
            onClick={this.claimHandler.bind(this, "openInEditor", undefined)}
            themeColor={'primary'}
          >
            {this.translate('claimAndOpen')}
          </PuzzleButton>
        } else if (editors.length > 1) {
          render = <PuzzleDropDownButton
            aria-label={this.translate('claimAndOpen')}
            icon={'arrow-s'}
            items={this.renderMultipleEditors(editors)}
            onItemClick={this.onMultiItemOpenClick}
            reverse
            text={this.translate('claimAndOpen')}
            themeColor={'primary'}
          />
        } else if (openDownloadEnabled && !editors.length) {
          render = <PuzzleButton
            onClick={this.claimHandler.bind(this, "downloadGLP", undefined)}
            icon={'tp-icon tp-download-thin'}
            reverse
            themeColor={'primary'}
          >
            {this.translate('claimAndOpen')}
          </PuzzleButton>
        }
      }
    }

    return render
  }

  /**
   * Renders editorNames for Open bttn
   * @param editors
   */
  renderMultipleEditors = (editors: string[]) => {
    return editors.map((editor) => {
      return {
        editor: editor,
        text: this.translate(`allFiles`, { editorName: this.translate(`editorNames.${editor}`) })
      }
    });
  };

  /**
   * Handler for dropdown items click.
   * @param element
   */
  onMultiItemOpenClick = (element: DropDownButtonItemClickEvent): void => {
    const {
      editor
    } = element.item;
    this.claimHandler('openInEditor', editor);
  };

  /**
   * Handler for Claim task button
   */
  claimHandler = (claimAndOpen: boolean | string, editor: string | undefined) => {
    this.dispatch('claimSupplier', '', {
      submissionData: this.props.submissionData,
      claimAndOpen,
      editor
    });
  };

  /**
   * Claim grid cell render
   * @param defaultRendering
   * @param cellProps
   */
  claimCellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps):
    JSX.Element => {
    const { dataItem, field } = cellProps;
    let children;
    let editedProps = {
      ...defaultRendering?.props,
      className: `${defaultRendering?.props.className} ${this.isCellDisabled(cellProps)}`
    };

    if (defaultRendering && field) {
      children = this.getCellRender(cellProps, field, dataItem);
    }

    return React.cloneElement((defaultRendering as any), editedProps, children);
  };

  /**
   * Adds disable class to cell if needs to be deisabled
   */
  isCellDisabled = (cellProps: GridCellProps): string => {
    return '';
  };

  /**
   * Gets proper render for given field
   * @param cellProps
   * @param field
   * @param dataItem
   */
  getCellRender(cellProps: any, field: any, dataItem: any) {
    let render;
    switch (cellProps.field) {
      case 'batchName':
      case 'targetLanguageDisplayName':
        render = defaultCellRender(this.translate, () => null, cellProps);
        break;
      case 'startDate':
      case 'dueDate':
        render = this.dateRender(field, cellProps);
        break;
      case 'wordCount':
        render = this.wordCountRender(cellProps);
        break;
      default:
        render = dataItem[field];
        break;
    }

    return render;
  }

  /**
   * Returns data for toast notification
   */
  getNotificationData = () => {
    const { data } = this.state;
    const submissionPhase = this.props.responseData.phase
    const taskPhaseLength = data.filter((item: any) => item.phaseName === submissionPhase).length;
    const reservePhaseLength = data.length - taskPhaseLength;

    let notificationCase = this.props.isClaim ? 'claim' : 'unclaim';
    return { notificationCase, taskPhaseLength, reservePhaseLength };
  };

  /**
   * If we have combined phases, different batches or reserve action available claim and open should be hidden.
   */
  getClaimAndOpenButtonState = (): boolean => {
    const { batchLanguagePhaseDtoList = [] } = this.props.responseData.data;
    let visible = this.props.isClaim && batchLanguagePhaseDtoList.every((current: any) => current.phaseName === batchLanguagePhaseDtoList[0].phaseName && current.batchName === batchLanguagePhaseDtoList[0].batchName);
    visible = visible && !isActionAvailable(this.props.submissionData, 'reserve job phase');

    return visible;
  };


  /**
   * Data for post
   */
  getData = () => {
    const { data } = this.state;
    let postDataArray;
    const taskIdArray = data.map((item: any) => {
      return item.taskId
    })
    postDataArray = {
      phaseName: this.props.responseData.phase,
      taskIds: taskIdArray
    }
    return postDataArray
  }

  /**
   * Opens word count dialog
   */
  openWordCount = () => {
    const { submissionData } = this.props;
    this.dispatch('openSupplierWordCount', '', {
      wordCountParams: {
        taskId: submissionData.taskId
      },
      submissionName: submissionData.submissionName,
      submissionId: submissionData.submissionId,
      submissionData
    });
  };

  /**
   * Render claim task footer buttons
   */
  renderFooterButtons = () => {
    return this.props.isClaim ?
      <>
        {this.displayBadge &&
          <Notification
            className={'tp-informational'}
            type={{ style: 'info', icon: true }}
          >
                <span>
                  {this.translate('disclaimer', { className: 'tp-text-semibold' })}
                </span>
          </Notification>}
        <Container className={'k-flex-1'} />
        <PuzzleButton
          onClick={() => this.dispatch('closeClaimSupplierDialog')}
          fillMode={'flat'}>
          {this.translate('cancel')}
        </PuzzleButton>
        <PuzzleButton
          onClick={this.claimHandler.bind(this, false, undefined)}
          themeColor={'primary'}>
          {this.translate('claimTask')}
        </PuzzleButton>
        {this.claimAndOpenRender()}
      </>
      : <>
        <Notification
          className={'tp-informational'}
          type={{ style: 'warning', icon: true }}
        >
          {this.translate('unclaimBadge')}
        </Notification>
        <Container className={'k-flex-1'} />
        <PuzzleButton
          onClick={() => this.dispatch('closeClaimSupplierDialog')}
          fillMode={'flat'}>
          {this.translate('cancel')}
        </PuzzleButton>
        <PuzzleButton
          onClick={() => this.dispatch('unclaimTask')}
          themeColor={'error'}>
          {this.translate('unclaimTask')}
        </PuzzleButton>
      </>

  };

  /**
   * Render claim grid columns
   */
  renderColumns() {
    const columnCommonProps: any = {
      minResizableWidth: 100,
      className: 'tp-sortable',
      headerClassName: 'tp-sortable'
    };

    return [
      <KendoGridColumn
        key={"targetLanguageDisplayName_key"}
        field="targetLanguageDisplayName"
        title={this.translate('columnHeaders.targetLanguageDisplayName')}
        width="150px"
        {...columnCommonProps}
      />,
      <KendoGridColumn
        key={"batchName_key"}
        field="batchName"
        width="160px"
        title={this.translate('columnHeaders.batchName')}
        {...columnCommonProps}
      />,
      <KendoGridColumn
        key={"phaseName_key"}
        field="phaseName"
        title={this.translate('columnHeaders.phaseName')}
        width="150px"
        {...columnCommonProps}
      />,
      <KendoGridColumn
        key={"startDate_key"}
        field="startDate"
        title={this.translate('columnHeaders.startDate')}
        width="160px"
        {...columnCommonProps}
      />,
      <KendoGridColumn
        key={"dueDate_key"}
        field="dueDate"
        className={'tp-text-semibold tp-sortable'}
        title={this.translate('columnHeaders.dueDate')}
        minResizableWidth={100}
        headerClassName={'tp-sortable'}
        width="156px"
      />,
      <KendoGridColumn
        key={"wordCount_key"}
        field="wordCount"
        title={this.translate('columnHeaders.wordCount')}
        width="130px"
        {...columnCommonProps}
      />,
      <KendoGridColumn
        key={"fileCount_key"}
        field="fileCount"
        title={this.translate('columnHeaders.files')}
        width="84px"
        {...columnCommonProps}
      />
    ]
  }

  renderBody() {
    const { data, claimLvl } = this.state;


    return (
      <VBox justifyContentBetween className={'tp-claim-task-content'}>
        <VBox className={'tp-claim-content-inner'}>
          <HBox
            justifyContentBetween
            responsive={{
              sm: 'k-d-flex-col tp-gap-1'
            }}
            className="tp-claim-task-dialog-subheader"
          >
            <HBox alignItemsCenter>
            <span className="tp-text-semibold tp-font-size-md">
              {this.translate('claimLevel')}
            </span>
              <span className="tp-spacer">|</span>
              <span className='tp-text-color-light'>
              {(claimLvl === 'LANGUAGE') ? this.translate('language') : this.translate('batch')}
            </span>
            </HBox>
          </HBox>
          <KendoGridAbstract
            captionText={this.translate('SRL-claimTableCaption')}
            className={`tp-no-border`}
            data={data}
            total={data.length}
            cellRender={this.claimCellRender}
            resizable={true}
          >
            {this.renderColumns()}
          </KendoGridAbstract>
        </VBox>
        <DialogActionsBar>
          <div className={'tp-dialog-footer-toolbar tp-left-and-right'}>
            {this.renderFooterButtons()}
          </div>
        </DialogActionsBar>
      </VBox>
    );
  }
}

export default ClaimTaskDialog;
